'use client';

import { Box, Button, Flex, IconButton, Text } from '@chakra-ui/react';
import { Download, FileCheck, TriangleAlert } from 'lucide-react';
import { toast } from 'sonner';
import { LoadingIndicator as Spinner } from '@/components/shared/LoadingIndicator';
import { formatCodexRelativeTime } from '@/lib/codex-workspace-format';
import { useDownloads } from './DownloadsProvider';

export function DownloadsList({ compact = false }: { compact?: boolean }) {
  const { items, loading, error, refresh, hasMore, loadMore, downloadAgain } = useDownloads();
  return (
    <Box aria-live="polite" aria-relevant="additions text">
      {error && (
        <Flex role="alert" align="center" gap="2" px="4" py="3">
          <Text fontSize="sm" color="status.warning.fg" flex="1">
            {error}
          </Text>
          <Button size="xs" variant="ghost" onClick={refresh}>
            Retry
          </Button>
        </Flex>
      )}
      {loading && (
        <Flex role="status" gap="2" align="center" px="4" py="3">
          <Spinner size="xs" />
          <Text fontSize="xs">Loading downloads…</Text>
        </Flex>
      )}
      {!items.length && !loading && !error && (
        <Box px="4" py="8" textAlign="center">
          <Text fontSize="sm">No downloads yet</Text>
          <Text fontSize="xs" color="ui.text.secondary" mt="1">
            Files you export from this project will appear here.
          </Text>
        </Box>
      )}
      <Box as="ul" listStyleType="none" m="0" p="0" aria-label="Project downloads">
        {items.map(item => {
          const active = item.status === 'preparing' || item.status === 'downloading';
          const label =
            item.status === 'preparing'
              ? 'Preparing export…'
              : item.status === 'downloading'
                ? 'Downloading file…'
                : item.status === 'failed'
                  ? 'Download failed'
                  : item.saved
                    ? 'Saved'
                    : 'Sent to browser';
          return (
            <Flex
              as="li"
              key={item.id}
              gap="3"
              px="4"
              py="3"
              align="center"
              borderBottomWidth="1px"
              borderColor="ui.border.subtle"
              _last={{ borderBottomWidth: 0 }}
            >
              <Box color={item.status === 'failed' ? 'status.warning.fg' : 'ui.text.secondary'}>
                {active ? (
                  <Spinner size="sm" />
                ) : item.status === 'failed' ? (
                  <TriangleAlert size={16} />
                ) : (
                  <FileCheck size={16} />
                )}
              </Box>
              <Box minW="0" flex="1">
                <Text fontSize="sm" fontWeight="medium" overflowWrap="anywhere">
                  {item.name}
                </Text>
                {!compact && item.filename && (
                  <Text fontSize="xs" color="ui.text.secondary" overflowWrap="anywhere">
                    {item.filename}
                  </Text>
                )}
                <Text fontSize="xs" color="ui.text.secondary" mt="1">
                  {item.format.toUpperCase()} · {label} · {formatCodexRelativeTime(item.createdAt)}
                  {!compact && item.sizeBytes !== undefined
                    ? ` · ${item.sizeBytes < 1024 * 1024 ? `${Math.max(1, Math.round(item.sizeBytes / 1024))} KB` : `${(item.sizeBytes / (1024 * 1024)).toFixed(1)} MB`}`
                    : ''}
                </Text>
                {item.error && (
                  <Text fontSize="xs" color="status.warning.fg" mt="1" overflowWrap="anywhere">
                    {item.error}
                  </Text>
                )}
              </Box>
              {item.saved && (
                <IconButton
                  size="sm"
                  variant="ghost"
                  disabled={active}
                  aria-label={`Download ${item.filename || item.name} again`}
                  title="Download again"
                  onClick={() =>
                    void downloadAgain(item).catch(error =>
                      toast.error(
                        error instanceof Error ? error.message : 'Download failed. Try again.'
                      )
                    )
                  }
                >
                  <Download size={16} />
                </IconButton>
              )}
            </Flex>
          );
        })}
      </Box>
      {hasMore && (
        <Box textAlign="center" p="3">
          <Button size="sm" variant="ghost" disabled={loading} onClick={loadMore}>
            Load older downloads
          </Button>
        </Box>
      )}
    </Box>
  );
}
